'use client';

/**
 * ClubesFilterBar — FilterBar do /clubes (Figma 194:4915 "form").
 *
 * Layout dual (mobile-tweaks-v2 lote 2 — Bruno 2026-05-21):
 *
 * Desktop (md+):
 *   Grupo 1 (filtros de dados):
 *     - Input busca — bg-white rounded-24, leadingIcon Search
 *     - Select bairro — bg-white rounded-24, MapPin + "Bairro:" + value
 *   Grupo 2 (toggles):
 *     - Switch Brisa Card — VerifiedBadge 32px + Switch
 *     - Switch Mapa — Map icon + "Mapa" + Switch (controla view)
 *
 * Mobile (< md):
 *   - Input busca full-width (deve ficar sticky via wrapper externo no ClubesView)
 *   - Botão icon-circle (SlidersHorizontal) ao lado abre DropdownMenu suspenso
 *     com bairros (lista de items) + Partner Switch.
 *   - SEM toggle Lista/Mapa — em mobile mapa+lista coexistem sempre (ClubesView).
 */
import * as React from 'react';
import { MapPin, Map as MapIcon, Search, SlidersHorizontal } from 'lucide-react';

import { Button } from '@brisa/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@brisa/ui/components/ui/dropdown-menu';
import { Input } from '@brisa/ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@brisa/ui/components/ui/select';
import { Switch } from '@brisa/ui/components/ui/switch';
import { VerifiedBadge } from '@brisa/ui/components/facelift/verified-badge/VerifiedBadge';

export const NEIGHBORHOOD_ALL = '__all__';

export interface ClubesFilterBarCopy {
  searchPlaceholder: string;
  neighborhoodLabel: string;
  neighborhoodAll: string;
  brisaCardLabel: string;
  mapaLabel: string;
}

export interface ClubesFilters {
  search: string;
  neighborhood: string;
  partnerOnly: boolean;
}

export type ClubesView = 'list' | 'map';

export interface ClubesFilterBarProps {
  copy: ClubesFilterBarCopy;
  neighborhoods: string[];
  filters: ClubesFilters;
  onFiltersChange: (next: ClubesFilters) => void;
  view: ClubesView;
  onViewChange: (next: ClubesView) => void;
}

export function ClubesFilterBar({
  copy,
  neighborhoods,
  filters,
  onFiltersChange,
  view,
  onViewChange,
}: ClubesFilterBarProps) {
  return (
    <>
      {/* ============ Mobile (< md) ============ */}
      <div
        data-slot="clubes-filterbar-mobile"
        className="flex w-full items-center gap-2 md:hidden"
      >
        <Input
          type="search"
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          placeholder={copy.searchPlaceholder}
          leadingIcon={
            <Search size={16} strokeWidth={1.5} className="text-[hsl(var(--text-100))]" />
          }
          aria-label={copy.searchPlaceholder}
          className="h-12 flex-1 rounded-3xl shadow-2xl"
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="icon-circle"
              size="icon"
              aria-label="Filtros"
              data-slot="filter-trigger"
              className="h-12 w-12 shrink-0"
            >
              <SlidersHorizontal size={18} aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-[280px] p-3"
            data-slot="filter-menu"
          >
            <DropdownMenuLabel>{copy.neighborhoodLabel}</DropdownMenuLabel>
            <div className="max-h-[280px] overflow-y-auto" role="radiogroup">
              <DropdownMenuItem
                active={filters.neighborhood === NEIGHBORHOOD_ALL}
                onSelect={(e) => {
                  // preventDefault mantém o menu aberto após selecionar,
                  // permitindo flip do Switch partner no mesmo "uso".
                  e.preventDefault();
                  onFiltersChange({ ...filters, neighborhood: NEIGHBORHOOD_ALL });
                }}
                role="radio"
                aria-checked={filters.neighborhood === NEIGHBORHOOD_ALL}
              >
                {copy.neighborhoodAll}
              </DropdownMenuItem>
              {neighborhoods.length > 0 ? <DropdownMenuSeparator /> : null}
              {neighborhoods.map((n) => (
                <DropdownMenuItem
                  key={n}
                  active={filters.neighborhood === n}
                  onSelect={(e) => {
                    e.preventDefault();
                    onFiltersChange({ ...filters, neighborhood: n });
                  }}
                  role="radio"
                  aria-checked={filters.neighborhood === n}
                >
                  {n}
                </DropdownMenuItem>
              ))}
            </div>

            <DropdownMenuSeparator />

            {/* Linha do Switch — fora do DropdownMenuItem stack para evitar
                auto-close do Radix ao interagir. Click no row inteiro flipa o
                switch (UX mobile-friendly). */}
            <div
              className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
              data-slot="filter-partner-row"
              onClick={(e) => {
                // Evita propagação que poderia fechar o menu por outside-click
                e.stopPropagation();
              }}
            >
              <div className="flex items-center gap-2">
                <VerifiedBadge size={20} aria-hidden="true" />
                <span className="text-sm font-medium">{copy.brisaCardLabel}</span>
              </div>
              <Switch
                checked={filters.partnerOnly}
                onCheckedChange={(checked) =>
                  onFiltersChange({ ...filters, partnerOnly: checked })
                }
                aria-label={copy.brisaCardLabel}
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ============ Desktop (md+) ============ */}
      <div
        data-slot="clubes-filterbar-desktop"
        className="hidden flex-wrap items-center gap-6 md:flex"
      >
        {/* Grupo 1 — Filtros de dados */}
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          <Input
            type="search"
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            placeholder={copy.searchPlaceholder}
            leadingIcon={
              <Search size={16} strokeWidth={1.5} className="text-[hsl(var(--text-100))]" />
            }
            aria-label={copy.searchPlaceholder}
            className="h-12 w-full rounded-3xl sm:w-[280px]"
          />

          <Select
            value={filters.neighborhood}
            onValueChange={(value) => onFiltersChange({ ...filters, neighborhood: value })}
          >
            <SelectTrigger aria-label={copy.neighborhoodLabel} className="w-full sm:w-[240px]">
              <span className="flex items-center gap-2">
                <MapPin
                  size={16}
                  aria-hidden="true"
                  strokeWidth={1.5}
                  className="shrink-0 text-[hsl(var(--text-100))]"
                />
                <span className="text-[hsl(var(--text-40))]">{copy.neighborhoodLabel}</span>
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value={NEIGHBORHOOD_ALL}>{copy.neighborhoodAll}</SelectItem>
              {neighborhoods.length > 0 && <SelectSeparator />}
              {neighborhoods.map((n) => (
                <SelectItem key={n} value={n}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grupo 2 — Toggles */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Switch Brisa Card (Figma 194:5214) */}
          <div
            className="flex h-[52px] items-center gap-3 rounded-[96px] bg-white/[0.08] px-6 py-4"
            data-slot="filterbar-switch-brisa"
          >
            <VerifiedBadge size={32} aria-label={copy.brisaCardLabel} />
            <Switch
              checked={filters.partnerOnly}
              onCheckedChange={(checked) =>
                onFiltersChange({ ...filters, partnerOnly: checked })
              }
              aria-label={copy.brisaCardLabel}
            />
          </div>

          {/* Switch Mapa (Figma 194:5196) — desktop only; mobile sempre mostra
              mapa+lista juntos via ClubesView.forceMobileBoth */}
          <div
            className="flex h-[52px] items-center gap-4 rounded-[99px] bg-white/[0.08] px-6 py-4"
            data-slot="filterbar-switch-map"
          >
            <MapIcon
              size={16}
              aria-hidden="true"
              strokeWidth={1.5}
              className="shrink-0 text-[hsl(var(--accent-yellow))]"
            />
            <label
              htmlFor="filter-view-mapa"
              className="cursor-pointer text-sm leading-none text-[hsl(var(--text-100))]"
            >
              {copy.mapaLabel}
            </label>
            <Switch
              id="filter-view-mapa"
              checked={view === 'map'}
              onCheckedChange={(checked) => onViewChange(checked ? 'map' : 'list')}
              aria-label={copy.mapaLabel}
            />
          </div>
        </div>
      </div>
    </>
  );
}
